import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { createAuthHeaders } from '../../hooks/useSessionToken';
import { StatusBadge } from '../../components/StatusBadge';
import { MobileMenu, PARTNER_LINKS } from '../../components/MobileMenu';
import { API_ENDPOINTS } from '../../config/api';

interface Referral {
  id: string;
  clientName: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  commissionRon: number | null;
  serviceValueRon: number | null;
}

export function Referrals() {
  const { logout } = useAuth();
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    fetchReferrals();
  }, [page]);

  async function fetchReferrals() {
    try {
      const res = await fetch(`${API_ENDPOINTS.referrals}?page=${page}&limit=20`, {
        headers: createAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setReferrals(data.data);
        setTotalPages(data.totalPages);
      }
    } catch (err) {
      console.error('Error fetching referrals:', err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <nav className="bg-white border-b border-border sticky top-0">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 md:space-x-8">
              <MobileMenu links={PARTNER_LINKS} onLogout={logout} />
              <span className="font-serif text-xl text-teal">BrainMap Partner</span>
              <div className="hidden md:flex space-x-4">
                <Link to="/dashboard" className="text-muted hover:text-teal">Dashboard</Link>
                <Link to="/analytics" className="text-muted hover:text-teal">Analitică</Link>
                <Link to="/referrals" className="text-teal font-medium">Referral-uri</Link>
                <Link to="/invoices" className="text-muted hover:text-teal">Facturi</Link>
                <Link to="/my-link" className="text-muted hover:text-teal">Link-ul Meu</Link>
              </div>
            </div>
            <button onClick={logout} className="text-sm text-muted hover:text-ink">Deconectare</button>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-serif text-ink">Toate Referral-urile</h1>
          <Link to="/dashboard" className="text-teal hover:underline">← Înapoi la Dashboard</Link>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal"></div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase">Client</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase">Creat</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted uppercase">Actualizat</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase">Valoare</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-muted uppercase">Comision</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {referrals.map((ref) => (
                  <tr key={ref.id} className="hover:bg-warm/50">
                    <td className="px-6 py-4 font-medium">{ref.clientName}</td>
                    <td className="px-6 py-4"><StatusBadge status={ref.status} /></td>
                    <td className="px-6 py-4 text-sm text-muted">
                      {new Date(ref.createdAt).toLocaleDateString('ro-RO')}
                    </td>
                    <td className="px-6 py-4 text-sm text-muted">
                      {new Date(ref.updatedAt).toLocaleDateString('ro-RO')}
                    </td>
                    <td className="px-6 py-4 text-right">
                      {ref.serviceValueRon ? `${ref.serviceValueRon.toLocaleString('ro-RO')} RON` : '—'}
                    </td>
                    <td className="px-6 py-4 text-right font-medium text-teal">
                      {ref.commissionRon ? `${ref.commissionRon.toLocaleString('ro-RO')} RON` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {totalPages > 1 && (
              <div className="px-6 py-4 flex justify-between items-center border-t border-border">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm text-muted hover:text-teal disabled:opacity-50"
                >
                  ← Pagina anterioară
                </button>
                <span className="text-sm text-muted">Pagina {page} din {totalPages}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm text-muted hover:text-teal disabled:opacity-50"
                >
                  Pagina următoare →
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
